import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ShieldCheck, Lock, ArrowRight, Zap, Star, Crown, Rocket } from "lucide-react";
import Seo from "@/components/Seo";
import BoostPricingModal from "@/components/BoostPricingModal";
import { FadeUp } from "@/components/Reveal";

const TIERS = [
  {
    id: "discover_boost",
    tag: "Starter",
    tagIcon: <Zap size={9} />,
    tagCls: "text-slate-500 bg-gray-100 border-gray-200",
    label: "Discover Boost",
    price: "$4.99",
    period: "for 7 days",
    priceCls: "text-slate-900",
    cardCls: "border border-gray-200 bg-white shadow-sm hover:shadow-md",
    barCls: null,
    features: null,
    options: [
      { label: "Option A", desc: "Sponsored Discover page slot — social post or product video" },
      { label: "Option B", desc: "Leaderboard video placement — video upload" },
    ],
    note: "Sponsored tag always visible. Never affects Rank Position.",
    noteIcon: false,
    btnCls: "bg-slate-900 hover:bg-slate-700 text-white",
    testId: "pricing-page-discover-boost-cta",
    featured: false,
    mostPopular: false,
  },
  {
    id: "boost",
    tag: "Growth",
    tagIcon: <Star size={9} />,
    tagCls: "text-slate-600 bg-slate-100 border-slate-200",
    label: "Boost",
    price: "$9.99",
    period: "for 10 days",
    priceCls: "text-slate-900",
    cardCls: "border-2 border-slate-800 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.10)] hover:shadow-[0_6px_32px_rgba(0,0,0,0.16)]",
    barCls: "bg-gradient-to-r from-slate-700 to-slate-900",
    features: [
      "Top Spot page placement",
      "2× sponsored Discover slots (bundled)",
      "Leaderboard video placement",
    ],
    featureIconCls: "bg-slate-100 border-slate-200",
    featureCheckCls: "text-slate-600",
    note: "Boosted tag always visible. Never affects Rank Position.",
    noteIcon: false,
    btnCls: "bg-slate-900 hover:bg-slate-700 text-white",
    testId: "pricing-page-boost-cta",
    featured: false,
    mostPopular: false,
  },
  {
    id: "pro_boost",
    tag: "Pro",
    tagIcon: <Crown size={9} />,
    tagCls: "text-emerald-700 bg-emerald-50 border-emerald-200",
    label: "Pro Boost",
    price: "$14.99",
    period: "for 10 days",
    priceCls: "text-emerald-600",
    cardCls: "border-2 border-emerald-500 bg-white shadow-[0_8px_40px_rgba(16,185,129,0.20)] hover:shadow-[0_12px_48px_rgba(16,185,129,0.30)] lg:scale-[1.03] lg:origin-center",
    barCls: "bg-gradient-to-r from-emerald-400 to-emerald-500",
    features: [
      "Everything in Boost",
      "Home screen video placement",
      "HighRank share card — branded shareable graphic",
      "Pro Alumni status — permanent badge + priority access",
    ],
    featureIconCls: "bg-emerald-100 border-emerald-300",
    featureCheckCls: "text-emerald-600",
    note: "Boosted tag always visible. Never affects Rank Position.",
    noteIcon: true,
    btnCls: "bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_4px_20px_rgba(16,185,129,0.35)]",
    testId: "pricing-page-pro-boost-cta",
    featured: true,
    mostPopular: true,
  },
];

export default function Pricing() {
  const [pricingOpen, setPricingOpen] = useState(false);
  const [activeTier, setActiveTier] = useState(null);

  const openTier = (tier) => {
    setActiveTier(tier);
    setPricingOpen(true);
  };

  return (
    <>
      <Seo
        title="Pricing — HighRank Visibility Boosts"
        description="Optional visibility upgrades for your HighRank listing. Choose Discover Boost, Boost, or Pro Boost. None affect your HighRank Score or Rank Position."
      />

      <div className="min-h-screen bg-white">
        {/* Page header */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-4 text-center">
          <FadeUp>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-600 mb-3">Visibility Upgrades</p>
            <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-wide text-slate-900">
              Optional Boosts
            </h1>
            <p className="mt-4 text-sm text-slate-500 max-w-xl mx-auto leading-relaxed">
              Discover listings are always free. These are optional visibility upgrades —
              none of them affect your HighRank Score or Rank Position.
            </p>
          </FadeUp>
        </div>

        {/* Pricing cards */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid lg:grid-cols-3 gap-6 items-stretch">
            {TIERS.map((tier, i) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: "easeOut", delay: i * 0.08 }}
                className={`relative flex flex-col rounded-2xl overflow-hidden transition-shadow ${tier.cardCls}`}
              >
                {/* Coloured top bar */}
                {tier.barCls && <div className={`h-1.5 ${tier.barCls}`} />}

                {/* Most popular badge */}
                {tier.mostPopular && (
                  <div className="absolute top-0 right-6 translate-y-3">
                    <span className="inline-flex items-center gap-1 bg-emerald-500 text-white font-black text-[9px] uppercase tracking-widest rounded-full px-3 py-1.5 shadow-lg">
                      <Rocket size={9} /> Most Popular
                    </span>
                  </div>
                )}

                <div className={`px-7 flex-1 flex flex-col ${tier.barCls ? "pt-6" : "pt-7"} pb-0`}>
                  {/* Tag */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-widest border rounded-full px-2.5 py-1 ${tier.tagCls}`}>
                      {tier.tagIcon} {tier.tag}
                    </span>
                  </div>

                  {/* Title + price */}
                  <h3 className="font-display text-2xl uppercase tracking-wide text-slate-900 mb-1">{tier.label}</h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className={`text-5xl font-black leading-none ${tier.priceCls}`}>{tier.price}</span>
                    <span className="text-sm text-slate-400 self-end mb-1">{tier.period}</span>
                  </div>

                  {/* Features or options */}
                  {tier.options ? (
                    <div className="space-y-2 mb-6">
                      {tier.options.map(({ label, desc }) => (
                        <div key={label} className="flex gap-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                          <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                            <Check size={10} className="text-slate-500" />
                          </span>
                          <div>
                            <p className="font-mono2 text-[9px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</p>
                            <p className="text-xs text-slate-600 leading-snug">{desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="space-y-3 mb-6 flex-1">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5">
                          <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${tier.featureIconCls}`}>
                            <Check size={10} className={tier.featureCheckCls} />
                          </span>
                          <span className="text-sm text-slate-700 font-medium">{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Note */}
                  <p className={`text-[11px] flex items-start gap-1.5 mb-7 ${tier.noteIcon ? "text-slate-500" : "text-slate-400"}`}>
                    <ShieldCheck size={11} className={`shrink-0 mt-0.5 ${tier.noteIcon ? "text-emerald-500" : ""}`} />
                    {tier.note}
                  </p>
                </div>

                {/* CTA */}
                <div className="px-7 pb-7">
                  <button
                    data-testid={tier.testId}
                    onClick={() => openTier(tier.id)}
                    className={`w-full flex items-center justify-center gap-2 font-bold text-sm rounded-full py-3.5 transition-all duration-200 group ${tier.btnCls}`}
                  >
                    Get Started <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Review disclosure */}
          <p className="text-center text-xs text-slate-400 mt-10 leading-relaxed max-w-lg mx-auto">
            Submitted content is reviewed within 24 hours. Content that doesn't meet our guidelines will be refunded in full.
          </p>

          {/* Stripe badge */}
          <div className="flex items-center justify-center gap-1.5 mt-5 opacity-40">
            <Lock size={11} className="text-slate-400" />
            <span className="text-[11px] text-slate-400 tracking-wide">Payments powered by</span>
            <svg viewBox="0 0 512 214" className="h-[15px] w-auto" aria-label="Stripe">
              <path fill="#635bff" d="M512 110.08c0-36.409-17.636-65.138-51.342-65.138-33.85 0-54.33 28.73-54.33 64.854 0 42.808 24.179 64.426 58.88 64.426 16.925 0 29.725-3.84 39.396-9.244v-28.445c-9.67 4.836-20.764 7.823-34.844 7.823-13.796 0-26.027-4.836-27.591-21.618h69.547c0-1.85.284-9.245.284-12.658m-70.258-13.511c0-16.071 9.814-22.756 18.774-22.756 8.675 0 17.92 6.685 17.92 22.756zm-90.31-51.627c-13.939 0-22.899 6.542-27.876 11.094l-1.85-8.818h-31.288v165.83l35.555-7.537.143-40.249c5.12 3.698 12.657 8.96 25.173 8.96 25.458 0 48.64-20.48 48.64-65.564-.142-41.245-23.609-63.716-48.498-63.716m-8.534 97.991c-8.391 0-13.37-2.986-16.782-6.684l-.143-52.765c3.698-4.124 8.818-6.968 16.925-6.968 12.942 0 21.902 14.506 21.902 33.137 0 19.058-8.818 33.28-21.902 33.28M241.493 36.551l35.698-7.68V0l-35.698 7.538zm0 10.809h35.698v124.444h-35.698zm-38.257 10.524L200.96 47.36h-30.72v124.444h35.556V87.467c8.39-10.951 22.613-8.96 27.022-7.396V47.36c-4.551-1.707-21.191-4.836-29.582 10.524m-71.112-41.386-34.702 7.395-.142 113.92c0 21.05 15.787 36.551 36.836 36.551 11.662 0 20.195-2.133 24.888-4.693V140.8c-4.55 1.849-27.022 8.391-27.022-12.658V77.653h27.022V47.36h-27.022zM35.982 83.484c0-5.546 4.551-7.68 12.09-7.68 10.808 0 24.461 3.272 35.27 9.103V51.484c-11.804-4.693-23.466-6.542-35.27-6.542C19.2 44.942 0 60.018 0 85.192c0 39.252 54.044 32.995 54.044 49.92 0 6.541-5.688 8.675-13.653 8.675-11.804 0-26.88-4.836-38.827-11.378v33.849c13.227 5.689 26.596 8.106 38.827 8.106 29.582 0 49.92-14.648 49.92-40.106-.142-42.382-54.329-34.845-54.329-50.774" />
            </svg>
          </div>
        </div>
      </div>

      <BoostPricingModal
        open={pricingOpen}
        onOpenChange={(v) => { setPricingOpen(v); if (!v) setActiveTier(null); }}
        company={null}
        initialTier={activeTier}
      />
    </>
  );
}
